import React from 'react';
import StyledComponent from '~/components/Styled/Styled';
import { ResponsivePNG } from 'sports-react';
import getOr from '~/util/getOr';

import css from './TimelineEntry.scss';

type Props = {
  timelineEvent: any;
  teams: {
    home: Team;
    away: Team;
  };
  away: boolean;
};

class TimelineEntry extends React.Component<Props> {
  static displayName: string = 'TimelineEntry';

  static iconTypes: object = {
    action_goal: 'goal.png',
    action_substitution: 'substitution.png',
    'yellow-card': 'yellowCard.png',
    'red-card': 'redCard.png',
    'yellow-red-card': 'redAndYellow.png',
    'shootout-goal': 'goal.png',
    'shootout-miss': 'penaltyMiss.png',
    'shootout-saved': 'penaltyMiss.png',
  };

  getTimelineIcon(timelineEvent: TimelineEvent, isPenalty: boolean) {
    if (isPenalty) {
      if (!timelineEvent.shootout_outcome) {
        return TimelineEntry.iconTypes['shootout-miss'];
      }
      return TimelineEntry.iconTypes[
        timelineEvent.shootout_outcome.replace(/\s+/g, '-').toLowerCase()
      ];
    }

    if (timelineEvent.type === 'action_card') {
      return TimelineEntry.iconTypes[timelineEvent.color];
    }
    return TimelineEntry.iconTypes[timelineEvent.type];
  }

  renderStackedEntry(
    event: TimelineEvent,
    playerPrimary: Player,
    playerSecondary: Player,
    progress: Progress,
    isAway: boolean,
    isGoal: boolean,
    goalType: string
  ) {
    const showScore =
      isGoal &&
      event.away_score_after !== null &&
      event.away_score_after !== undefined &&
      event.home_score_after !== null &&
      event.home_score_after !== undefined;

    return (
      <div className={`${css.substitution} ${css.entryContainer}`}>
        {isAway && (
          <div className={css.away}>
            <div className={css.names}>
              {playerPrimary && (
                <div className={`${css.playerPrimary} ${playerSecondary ? '' : css.singleName}`}>
                  {playerPrimary.first_initial_and_last_name}{' '}
                  {goalType === 'Own Goal' ? '(OG)' : null}
                </div>
              )}
              {!isGoal && <div className={css.greenArrowRight} />}
              <br />
              {playerSecondary && (
                <span className={css.playerSecondary}>
                  {playerSecondary.first_initial_and_last_name}
                </span>
              )}
              {!isGoal && <div className={css.redArrowLeft} />}
            </div>
            <span className={`${css.minute} ${playerSecondary ? '' : css.singleNameMinute}`}>
              {progress.clock_label}
            </span>
          </div>
        )}
        {!isAway && (
          <span>
            <span className={`${css.minute} ${playerSecondary ? '' : css.singleNameMinute}`}>
              {progress.clock_label}
            </span>
            <div className={css.names}>
              {!isGoal && <div className={css.greenArrowLeft} />}
              {playerPrimary && (
                <div className={`${css.playerPrimary} ${playerSecondary ? '' : css.singleName}`}>
                  {playerPrimary.first_initial_and_last_name}
                </div>
              )}
              <br />
              {!isGoal && <div className={css.redArrowRight} />}
              {playerSecondary && (
                <span className={css.playerSecondary}>
                  {playerSecondary.first_initial_and_last_name}
                </span>
              )}
            </div>
          </span>
        )}
        {showScore && (
          <div
            className={`${isAway ? css.gameScoreAway : css.gameScoreHome} ${
              playerSecondary ? '' : css.singleNameScore
            }`}
          >
            {this.props.teams.home.abbreviation}&nbsp;{event.home_score_after}&nbsp;-&nbsp;
            {event.away_score_after}&nbsp;{this.props.teams.away.abbreviation}
          </div>
        )}
      </div>
    );
  }

  renderEntry(event: TimelineEvent, progress: Progress, isAway: boolean, isPenalty: boolean) {
    const penaltyMiss =
      isPenalty && (event.shootout_outcome === 'Shootout Miss' || !event.shootout_outcome);

    return (
      <div className={`${css.entry} ${css.entryContainer} ${isAway ? css.away : ''}`}>
        {!isAway && !isPenalty && <span className={css.entryMinute}>{progress.clock_label}</span>}
        <div
          className={`${css.name} ${penaltyMiss ? css.penaltyMiss : ''}
          }`}
        >
          {event.player.first_initial_and_last_name}
        </div>
        {isAway && !isPenalty && <span className={css.entryMinute}>{progress.clock_label}</span>}
      </div>
    );
  }

  renderEvent(event, isPenalty) {
    const isSubstitution: boolean = event.type === 'action_substitution';
    const isAway: boolean = this.props.away;
    const isGoal: boolean = event.type === 'action_goal';
    const icon = this.getTimelineIcon(event, isPenalty);
    const mainPlayer = isSubstitution ? event.player_in : event.player;
    const secondaryPlayer = isSubstitution ? event.player_out : event.assist;
    const goalType = getOr(event, 'goal_type', '');

    return (
      <span>
        {!isAway && (
          <ResponsivePNG src={`/static/images/soccer/${icon}`} alt="" className={css.icon} />
        )}
        {(isSubstitution || isGoal) &&
          this.renderStackedEntry(
            event,
            mainPlayer,
            secondaryPlayer,
            event.progress,
            isAway,
            isGoal,
            goalType
          )}
        {!isGoal && !isSubstitution && this.renderEntry(event, event.progress, isAway, isPenalty)}
        {isAway && (
          <ResponsivePNG src={`/static/images/soccer/${icon}`} alt="" className={css.icon} />
        )}
      </span>
    );
  }

  render() {
    const timelineEvent = this.props.timelineEvent;
    const isShootoutPenalty: boolean = getOr(timelineEvent, 'shootout_outcome', false);
    const isPenalty: boolean = timelineEvent.shot_type === 'penalty kick taken';

    if (timelineEvent.type === 'action_segment_end' || timelineEvent.outcome === 'not-yet-taken') {
      return null;
    }

    return (
      <div
        className={`${css.container} ${
          !this.props.away && !isShootoutPenalty ? css.alignRight : ''
        } ${isShootoutPenalty ? css.penalty : ''}`}
      >
        {!isShootoutPenalty && <div className={css.lineTop} />}
        {this.renderEvent(timelineEvent, isShootoutPenalty || isPenalty)}
        {!isShootoutPenalty && <div className={css.lineBottom} />}
      </div>
    );
  }
}

export default StyledComponent(TimelineEntry, [css]);
